import { OpenInFull } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Slide,
  TextField,
  TextareaAutosize,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { isNumber } from "is-number-ts";
import moment from "moment";
import "moment/locale/pt-br";
import React, { forwardRef, useCallback, useContext, useEffect, useState, useRef } from "react";
import { CamposSelect, ProjetosCamadasFormulariosCampo, Tabelas } from "../../../interfaces";
import { DashboardContext } from "../../../providers/Dashboard";
import { DebounceContext } from "../../../providers/Debounce";
import {
  getDescriptions,
  getDescriptionsByName,
  LookupFilter,
  verifyIfInscricaoOrNumeroCadastroAlreadyExists,
} from "../../../services/api";
import { getValue, patch } from "../../../services/db";
import { logUpdate } from "../../../services/log";
import { converteTipoCampo } from "../../../services/utils";
import { mapToOptions } from "../../../utils/mapToOptions";
import { normalizeWord } from "../../../utils/normalizeWord";
import { transformName } from "../../../utils/transformName";
import { useTextArea } from "./hooks";
import "./styles.css";

export default function Input(props: { tabela: Tabelas; campo: ProjetosCamadasFormulariosCampo }) {
  const { metadata, errors, setErrors, reloadFields, isLeitor } = useContext(DashboardContext);
  const { handleValidate } = useContext(DebounceContext);
  const { openDialog, setOpenDialog, styleTextArea, style, handleResize, textareaRef, textareaSize } = useTextArea();
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const limitRadioGroup = 10;

  const theme = useTheme();

  const [value, setValue] = useState<any>("");
  const [isLoad, setLoad] = useState<boolean>(false);
  const [commaWarning, setCommaWarning] = useState<boolean>(false);

  const [search, setSearch] = useState<string | undefined>(undefined);
  const [options, setOptions] = useState<CamposSelect[]>([]);

  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);

  const lastParentId = useRef<any>(undefined);
  const hasParentFilter = !!props.campo.filtraCampo;

  const getParentValue = useCallback(() => {
    if (!hasParentFilter || !metadata[`id_${props.tabela}`]) return "";

    const raw = getValue(
      metadata[`id_${props.tabela}`]!,
      props.tabela,
      props.campo.filtraCampo!,
    );

    return String(raw ?? "");
  }, [hasParentFilter, metadata, props.tabela, props.campo.filtraCampo]);

  const [isParentEmpty, setIsParentEmpty] = useState<boolean>(() => {
    const parentValue = getParentValue();
    return hasParentFilter && (!parentValue || parentValue === "-1");
  });

  const inputId = `${metadata[`id_${props.tabela}`]!}@${props.tabela}@${props.campo.tblNomeColuna}`;
  const errorSeverity = errors?.[inputId]?.severity;

  // Campos que nao serão carregados no input
  const excludedFields = ["geom"];
  const isCepOrCpf = /cep|cpf_cnpj/i.test(props.campo.tblNomeColuna);

  function sanitizeInput(str: string): string {
    return str.replace(/[.\-\/\s]/g, '');
  }

  function formatCpfCnpjOrCep(value: string, tipo: "cpf_cnpj" | "cep"): string {
    if (tipo === "cep") return value.replace(/^(\d{5})(\d{3})$/, '$1-$2');
    if (value.length === 11) return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (value.length === 14) return value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return value;
  }

  const validate = useCallback(
    async (value: any) => {
      if (!inputId || inputId.startsWith("undefined")) return;

      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const isNovoRegistro = uuidRegex.test(metadata[`id_${props.tabela}`]);

      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(async () => {
        if (
          (props.campo.tblNomeColuna === "inscricao_cartografica" || props.campo.tblNomeColuna === "numero_cadastro") &&
          value !== ""
        ) {
          const valorNaoUnico = await verifyIfInscricaoOrNumeroCadastroAlreadyExists(
            props.tabela,
            value,
            props.campo.tblNomeColuna,
            metadata[`id_${props.tabela}`]!,
            { idImobiliario: metadata.id_imobiliario },
          );

          if (valorNaoUnico) {
            let storageErrors = JSON.parse(localStorage.errors);
            storageErrors = {
              ...storageErrors,
              [inputId]: {
                tabela: props.tabela,
                coluna: props.campo.tblNomeColuna,
                id: metadata[`id_${props.tabela}`]!,
                message: `O valor ${value} já está sendo utilizado`,
                severity: "error",
              },
            };

            setErrors(storageErrors);
            localStorage.errors = JSON.stringify(storageErrors);
          } else {
            let storageErrors = JSON.parse(localStorage.errors);
            delete storageErrors?.[inputId];
            localStorage.setItem("errors", JSON.stringify(storageErrors));
            setErrors(storageErrors);
          }
        } else if ((sanitizeInput(String(value || "")).trim() === "" || value === "-1") && props.campo.obrigatorio) {
          let storageErrors = JSON.parse(localStorage.errors);
          if (isNovoRegistro) {
            storageErrors = {
              ...storageErrors,
              [inputId]: {
                tabela: props.tabela,
                coluna: props.campo.tblNomeColuna,
                id: metadata[`id_${props.tabela}`]!,
                message: "Campo obrigatório",
                severity: "error",
              },
            };
          } else {
            storageErrors = {
              ...storageErrors,
              [inputId]: {
                tabela: props.tabela,
                coluna: props.campo.tblNomeColuna,
                id: metadata[`id_${props.tabela}`]!,
                message: "Campo obrigatório",
                severity: "warning",
              },
            };
          }
          localStorage.errors = JSON.stringify(storageErrors);
          setErrors(storageErrors);
        } else if (props.campo.limiteCaracteres && value && value.length > props.campo.limiteCaracteres) {
          const valorLimpo = isCepOrCpf ? sanitizeInput(value) : value;
          if (valorLimpo.length > props.campo.limiteCaracteres) {
            let storageErrors = JSON.parse(localStorage.errors || '{}');
            storageErrors = {
              ...storageErrors,
              [inputId]: {
                tabela: props.tabela,
                coluna: props.campo.tblNomeColuna,
                id: metadata[`id_${props.tabela}`]!,
                message: `O valor excede o limite de ${props.campo.limiteCaracteres} caracteres`,
                severity: "error",
              },
            };
            localStorage.errors = JSON.stringify(storageErrors);
            setErrors(storageErrors);
            return;
          }
        } else {
          let storageErrors = JSON.parse(localStorage.errors);
          delete storageErrors?.[inputId];
          localStorage.setItem("errors", JSON.stringify(storageErrors));
          setErrors(storageErrors);
        }
      }, 500);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.campo, setErrors, metadata, inputId],
  );

  const inputProps = {
    fullWidth: true,
    label: props.campo.nome,
    disabled:
      isLeitor ||
      props.campo.somenteVisualizacao ||
      !metadata[`id_${props.tabela}`] ||
      isParentEmpty,
    value: value ?? getValue(metadata[`id_${props.tabela}`]!, props.tabela, props.campo.tblNomeColuna),
    required: props.campo.obrigatorio,
    error: !!errors?.[inputId] && errorSeverity !== "warning",
    helperText:
      isParentEmpty && props.campo.filtraCampo
        ? `Selecione ${props.campo.filtraCampo} primeiro`
        : errors?.[inputId]?.message,
    isParentEmpty,
    className: `input-root ${errorSeverity === "error" ? "error" : errorSeverity === "warning" && value === "" ? "warning" : ""}`,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!metadata[`id_${props.tabela}`]) return; // casos onde o id ainda não foi definido ou a filtragem de tabs nao retornou nada

      const rawValue = e.target.value;
      const cleanValue = isCepOrCpf ? sanitizeInput(rawValue) : rawValue

      setValue(
        isCepOrCpf
          ? formatCpfCnpjOrCep(cleanValue, props.campo.tblNomeColuna.includes("cep") ? "cep" : "cpf_cnpj")
          : rawValue
      );
      patch(metadata[`id_${props.tabela}`]!, props.tabela, props.campo.tblNomeColuna, cleanValue);
      logUpdate({
        id_origem: metadata[`id_${props.tabela}`]!,
        nome_tabela: props.tabela,
        nome_campo: props.campo.tblNomeColuna,
        valor_campo: cleanValue || null,
        tipo_campo: converteTipoCampo(props.campo.tipo),
      });

      window.dispatchEvent(
        new CustomEvent("field-updated", {
          detail: {
            tabela: props.tabela,
            coluna: props.campo.tblNomeColuna,
            value: cleanValue,
          },
        })
      );

      if (props.campo.tblNomeColuna !== "inscricao_cartografica" && props.campo.tblNomeColuna !== "numero_cadastro") {
        validate(rawValue);
      }
    },
    InputLabelProps: {
      shrink: Boolean(value),
      sx: {
        "&.Mui-focused": {
          color: theme.palette.secondary.main,
        },
      },
    },
  };

  const inputDateProps = {
    value: value ? moment(value) : undefined,
    onChange: (e: any) => {
      if (!metadata[`id_${props.tabela}`]) return;
      setValue(e._d);
      patch(metadata[`id_${props.tabela}`]!, props.tabela, props.campo.tblNomeColuna, e._d);
      logUpdate({
        id_origem: metadata[`id_${props.tabela}`]!,
        nome_tabela: props.tabela,
        nome_campo: props.campo.tblNomeColuna,
        valor_campo: e._d,
        tipo_campo: converteTipoCampo(props.campo.tipo),
      });
      validate(e.target);
    },
  };

  useEffect(() => {
    if (!metadata[`id_${props.tabela}`]) return; // casos onde o id ainda não foi definido ou a filtragem de tabs nao retornou nada

    if (excludedFields.includes(props.campo.tblNomeColuna)) return;

    if (value === "" && !isLoad) {
      let retrievedValue = getValue(metadata[`id_${props.tabela}`]!, props.tabela, props.campo.tblNomeColuna);

      if (props.campo.tipo === 5 && retrievedValue === null) {
        retrievedValue = "-1";
        validate(retrievedValue);
      }

      if (retrievedValue) {
        const clean = String(retrievedValue);
        const formatted = isCepOrCpf
          ? formatCpfCnpjOrCep(clean, props.campo.tblNomeColuna.includes("cep") ? "cep" : "cpf_cnpj")
          : clean;
        setValue(formatted);
        validate(formatted);
      }
    } else {
      let retrievedValue = getValue(metadata[`id_${props.tabela}`]!, props.tabela, props.campo.tblNomeColuna);
      if (retrievedValue) {
        const clean = String(retrievedValue);
        const formatted = isCepOrCpf
          ? formatCpfCnpjOrCep(clean, props.campo.tblNomeColuna.includes("cep") ? "cep" : "cpf_cnpj")
          : clean;
        setValue(formatted);
        validate(formatted);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata, props.tabela, props.campo.tblNomeColuna, reloadFields, validate]);

  useEffect(() => {
    validate(value);
  }, [value]);

  function getLabelAndFetchFields(campo: ProjetosCamadasFormulariosCampo) {
    let labelFields = [campo.descricao].filter(Boolean) as string[];
    if (campo.descricao?.includes(",")) {
      labelFields = campo.descricao.split(",").map((s) => s.trim());
    }

    const needsFilterField = !!(campo.filtraCampo && campo.colunaFiltroRetorno);
    let fetchFields = [...labelFields];
    if (
      needsFilterField &&
      campo.colunaFiltroRetorno &&
      !fetchFields.includes(campo.colunaFiltroRetorno)
    ) {
      fetchFields.push(campo.colunaFiltroRetorno);
    }

    return { labelFields, fetchFields, needsFilterField };
  }

  async function fetchDescriptions() {
    setLoad(true);
    try {
      const campoName = transformName(props.campo.tblNomeColuna);
      const { labelFields, fetchFields, needsFilterField } = getLabelAndFetchFields(props.campo);

      const parentId = props.campo.filtraCampo
        ? getValue(metadata[`id_${props.tabela}`]!, props.tabela, props.campo.filtraCampo)
        : undefined;

      if (needsFilterField && (parentId == null || parentId === "" || String(parentId) === "-1")) {
        setIsParentEmpty(true);
        setOptions([]);
        setValue("");
        patch(metadata[`id_${props.tabela}`]!, props.tabela, props.campo.tblNomeColuna, null);
        props.campo.tipo = 8;
        return;
      }

      const filters: LookupFilter[] = [];
      if (needsFilterField && props.campo.colunaFiltroRetorno) {
        filters.push({
          column: props.campo.colunaFiltroRetorno,
          op: "eq",
          value: parentId,
        });
      }

      const data = await getDescriptions(campoName, fetchFields, value, filters);
      const allData = [...data];

      const dataOptions = mapToOptions(allData, labelFields);

      if (needsFilterField) {
        setOptions(dataOptions);
      } else {
        setOptions((prev) => [
          ...prev,
          ...dataOptions.filter((opt) => !prev.some((o) => o.id === opt.id)),
        ]);
      }

      props.campo.tipo = 8;
    } finally {
      setLoad(false);
    }
  }

  async function fetchDescriptionsByName() {
    let camposDescritores = [props.campo.descricao];
    if (props.campo.descricao.includes(",")) {
      camposDescritores = props.campo.descricao.split(",").map((s) => s.trim());
    }

    try {
      const parentId = props.campo.filtraCampo
        ? getValue(metadata[`id_${props.tabela}`]!, props.tabela, props.campo.filtraCampo)
        : undefined;

      const filters: LookupFilter[] = [];
      if (props.campo.colunaFiltroRetorno && parentId != null && parentId !== "") {
        filters.push({
          column: props.campo.colunaFiltroRetorno,
          op: "eq",
          value: parentId,
        });
      }

      const limit = 10;
      const offset = (page - 1) * limit;
      const orderBy = props.campo.descricao;
      let data: any[] = [];

      try {
        data = await getDescriptionsByName(
          transformName(props.campo.tblNomeColuna),
          search,
          camposDescritores,
          filters,
          orderBy,
          offset
        );
      } catch { }

      const dataOptions = mapToOptions(data, camposDescritores);

      setOptions((prev) => {
        if (page === 1) {
          return dataOptions;
        }

        const seen = new Set(prev.map((o) => String(o.id)));
        const appended = dataOptions.filter((o) => !seen.has(String(o.id)));
        return [...prev, ...appended];
      });

      setHasMore(data.length === limit);
    } catch (error) {
      console.error("Error fetching descriptions by name:", error);
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    if (props.campo.descricao) {
      fetchDescriptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.campo, value]);

  useEffect(() => {
    if (props.campo.descricao && search != null) {
      setPage(1);
      setHasMore(false);
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (props.campo.descricao && search != null) {
      fetchDescriptionsByName();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  useEffect(() => {
    if (!props.campo.filtraCampo) return;

    const handler = (e: CustomEvent) => {
      const { tabela, coluna, value } = e.detail || {};

      if (tabela === props.tabela && coluna === props.campo.filtraCampo) {
        const parentIsEmpty = !value || value === "-1" || value === "";

        setIsParentEmpty(parentIsEmpty);

        setValue("");
        patch(
          metadata[`id_${props.tabela}`]!,
          props.tabela,
          props.campo.tblNomeColuna,
          null,
        );

        setOptions([]);
        setSearch(undefined);
        setPage(1);
        setHasMore(false);

        if (parentIsEmpty) {
          lastParentId.current = undefined;
          return;
        }

        lastParentId.current = String(value);
        fetchDescriptions();
      }
    };

    window.addEventListener("field-updated", handler as EventListener);
    return () => window.removeEventListener("field-updated", handler as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tabela, props.campo.filtraCampo, metadata]);

  useEffect(() => {
    if (!props.campo.filtraCampo) return;

    const parentValue = getParentValue();
    const parentIsEmpty = !parentValue || parentValue === "-1";

    setIsParentEmpty(parentIsEmpty);

    if (parentIsEmpty) {
      setValue("");
      setOptions([]);
      setSearch(undefined);

      if (metadata[`id_${props.tabela}`]) {
        patch(
          metadata[`id_${props.tabela}`]!,
          props.tabela,
          props.campo.tblNomeColuna,
          null,
        );
      }
    }
  }, [
    getParentValue,
    metadata,
    props.tabela,
    props.campo.filtraCampo,
    props.campo.tblNomeColuna,
  ]);

  const handleLoadMoreMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleLoadMoreClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((prev) => prev + 1);
  };

  if (isLoad && !excludedFields.includes(props.campo.tblNomeColuna))
    return (
      <Card>
        <CardContent sx={{ padding: 2 }}>
          <Skeleton height={80} />
        </CardContent>
      </Card>
    );

  return (
    <>
      {!excludedFields.includes(props.campo.tblNomeColuna) && (
        <Card>
          <CardContent sx={{ padding: 2 }}>
            {props.campo.tipo === 1 && (
              <>
                <TextField
                  variant="filled"
                  inputMode="text"
                  {...inputProps}
                  onChange={(e) => {
                    handleValidate(e.target.value, validate);
                    inputProps.onChange(e);
                  }}
                  inputProps={{
                    maxLength: props.campo.limiteCaracteres,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <OpenInFull
                          sx={{
                            opacity: 0.5,
                          }}
                          onClick={() => setOpenDialog(true)}
                          style={{ cursor: "pointer" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                <Dialog
                  open={openDialog}
                  onClose={() => setOpenDialog(false)}
                  TransitionComponent={Transition}
                  maxWidth="xl"
                >
                  <Box sx={style}>
                    <Typography variant="h5">{props.campo.nome}</Typography>
                    <TextareaAutosize
                      ref={textareaRef}
                      {...inputProps}
                      style={{ ...styleTextArea, width: textareaSize.width, height: textareaSize.height }}
                      onChange={(e) => {
                        handleValidate(e.target.value, validate);
                        inputProps.onChange(e);
                      }}
                      onMouseUp={handleResize}
                      onKeyUp={handleResize}

                    />
                    <Button color="success" variant="contained" onClick={() => setOpenDialog(false)}>
                      Ok
                    </Button>
                  </Box>
                </Dialog>
              </>
            )}

            {props.campo.tipo === 2 && <TextField variant="filled" inputMode="numeric" {...inputProps} inputProps={{ maxLength: props.campo.limiteCaracteres }} />}

            {props.campo.tipo === 3 && (
              <TextField
                variant="filled"
                inputMode="decimal"
                {...inputProps}
                inputProps={{ maxLength: props.campo.limiteCaracteres }}
                onKeyDown={(e) => {
                  if (e.key === "," || e.key === "Decimal" || e.keyCode === 188) {
                    e.preventDefault();
                    setCommaWarning(true);
                    setTimeout(() => setCommaWarning(false), 3000);
                  }
                }}
                helperText={
                  commaWarning
                    ? 'Use ponto "." em vez de vírgula para separar decimais'
                    : inputProps.helperText
                }
              />
            )}

            {props.campo.tipo === 5 && props.campo.camposSelect.length < limitRadioGroup && (
              <FormControl required={inputProps.required} error={inputProps.error} disabled={inputProps.disabled}>
                <Tooltip title={props.campo.nome} placement="right-end">
                  <FormLabel
                    sx={{
                      "&.Mui-focused": {
                        color: theme.palette.secondary.main,
                      },
                    }}
                  >
                    {props.campo.nome}
                  </FormLabel>
                </Tooltip>
                <RadioGroup value={inputProps.value} onChange={inputProps.onChange}>
                  {props.campo.camposSelect
                    .sort((a, b) => a.nome?.localeCompare(b.nome))
                    .map((option) => {
                      return (
                        <Tooltip key={option.nome} title={option.nome} placement="right-end">
                          <FormControlLabel
                            value={option.tblValorDb}
                            control={
                              <Radio
                                sx={{
                                  "&.Mui-checked": {
                                    color: theme.palette.secondary.main,
                                  },
                                }}
                              />
                            }
                            label={option.nome}
                          />
                        </Tooltip>
                      );
                    })}
                  <Tooltip key={`${props.campo.nome}_null`} title={"Não atribuído"} placement="right-end">
                    <FormControlLabel
                      value={"-1"}
                      control={
                        <Radio
                          sx={{
                            "&.Mui-checked": {
                              color: theme.palette.secondary.main,
                            },
                          }}
                        />
                      }
                      label={"Não atribuído"}
                    />
                  </Tooltip>
                </RadioGroup>
                <FormHelperText>{errors?.[inputId]?.message}</FormHelperText>
              </FormControl>
            )}

            {props.campo.tipo === 6 && (
              <FormControl disabled={inputProps.disabled}>
                <Tooltip title={props.campo.nome} placement="right-end">
                  <FormLabel
                    sx={{
                      "&.Mui-focused": {
                        color: theme.palette.secondary.main,
                      },
                    }}
                  >
                    {props.campo.nome}
                  </FormLabel>
                </Tooltip>
                <RadioGroup value={inputProps.value} onChange={inputProps.onChange}>
                  <Tooltip key={props.campo.nome + `-sim`} title={`Sim`} placement="right-end">
                    <FormControlLabel
                      value={true}
                      control={
                        <Radio
                          sx={{
                            "&.Mui-checked": {
                              color: theme.palette.secondary.main,
                            },
                          }}
                        />
                      }
                      label={`Sim`}
                    />
                  </Tooltip>
                  <Tooltip key={props.campo.nome + `-nao`} title={`Não`} placement="right-end">
                    <FormControlLabel
                      value={false}
                      control={
                        <Radio
                          sx={{
                            "&.Mui-checked": {
                              color: theme.palette.secondary.main,
                            },
                          }}
                        />
                      }
                      label={`Não`}
                    />
                  </Tooltip>
                </RadioGroup>
              </FormControl>
            )}

            {props.campo.tipo === 7 && (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker {...inputProps} {...inputDateProps} />
              </LocalizationProvider>
            )}

            {props.campo.tipo === 8 && (
              <FormControl variant="filled" sx={{ width: "100%" }} disabled={inputProps.disabled}>
                <Tooltip
                  title={options.find((item) => item.tblValorDb === value)?.nome || props.campo.nome}
                  placement="top"
                >
                  <div>
                    <InputLabel
                      sx={{
                        "&.Mui-focused": {
                          color: theme.palette.secondary.main,
                        },
                      }}
                    >
                      {inputProps.label}
                    </InputLabel>
                    <Select
                      {...inputProps}
                      disabled={inputProps.disabled}
                      value={String(inputProps.value || "")}
                      variant="filled"
                      onChange={(e) => inputProps.onChange(e as any)}
                      onClose={() => fetchDescriptions()}
                      MenuProps={{ autoFocus: false }}
                    >
                      {options.length > 0 && (
                        <ListSubheader>
                          <TextField
                            variant="standard"
                            autoFocus
                            inputMode="search"
                            fullWidth
                            onChange={(e) => setSearch(e.target.value || "")}
                            placeholder="Pesquisar..."
                            onKeyDown={(e) => {
                              if (e.key !== "Escape") e.stopPropagation();
                            }}
                          />
                        </ListSubheader>
                      )}

                      {options.length !== 0 ? (
                        options
                          .filter((item) => item.id)
                          .sort((a, b) => {
                            try {
                              if (isNumber(a.nome) && isNumber(b.nome)) return Number(a.nome) - Number(b.nome);
                              return a.nome?.localeCompare(b.nome);
                            } catch {
                              return 0;
                            }
                          })
                          .filter((item) =>
                            normalizeWord(item.nome || String(item.id)).includes(normalizeWord(search || "")),
                          )
                          .map((item, index) => (
                            <MenuItem key={`${item.nome}_${index}`} value={String(item.id)}>
                              {`${item.nome || item.id} (${item.id})`}
                            </MenuItem>
                          ))
                      ) : (
                        <MenuItem disabled key="no-options">
                          Nenhum dado encontrado
                        </MenuItem>
                      )}

                      {hasMore && (
                        <ListSubheader
                          disableSticky
                          onMouseDown={handleLoadMoreMouseDown}
                          sx={{ p: 0 }}
                        >
                          <Box
                            role="button"
                            tabIndex={-1}
                            onMouseDown={handleLoadMoreMouseDown}
                            onClick={handleLoadMoreClick}
                            sx={{
                              px: 1.5,
                              py: 0,
                              cursor: loadingMore ? "default" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 600,
                              fontSize: "0.9rem",
                              color: theme.palette.secondary.main,
                              userSelect: "none",
                              "&:hover": {
                                opacity: loadingMore ? 1 : 0.75,
                                textDecoration: "none",
                              },
                            }}
                          >
                            {loadingMore ? "Carregando..." : "Carregar mais..."}
                          </Box>
                        </ListSubheader>
                      )}
                    </Select>
                  </div>
                </Tooltip>
              </FormControl>
            )}

            {props.campo.camposSelect.length >= limitRadioGroup && !props.campo.descricao && (
              <FormControl variant="filled" sx={{ width: "100%" }} disabled={inputProps.disabled}>
                <Tooltip
                  title={props.campo.camposSelect.find((item) => item.tblValorDb === value)?.nome || "Não atribuído"}
                  placement="top"
                >
                  <div>
                    <InputLabel
                      sx={{
                        "&.Mui-focused": {
                          color: theme.palette.secondary.main,
                        },
                      }}
                    >
                      {inputProps.label}
                    </InputLabel>
                    <Select
                      {...inputProps}
                      disabled={inputProps.disabled}
                      variant="filled"
                      onChange={(e) => inputProps.onChange(e as any)}
                      onClose={() => setSearch(undefined)}
                      MenuProps={{ autoFocus: false }}
                      placeholder="Pesquisar..."
                    >
                      <ListSubheader>
                        <TextField
                          variant="standard"
                          autoFocus
                          inputMode="search"
                          fullWidth
                          onChange={(e) => setSearch(e.target.value || "")}
                          placeholder="Pesquisar..."
                          onKeyDown={(e) => {
                            if (e.key !== "Escape") {
                              e.stopPropagation();
                            }
                          }}
                        />
                      </ListSubheader>
                      {props.campo.camposSelect.length !== 0 ? (
                        props.campo.camposSelect
                          .filter((item) => normalizeWord(item.nome).includes(normalizeWord(search || "")))
                          .map((item, index) => {
                            return (
                              <MenuItem key={`${item.nome}_${index}`} value={item.tblValorDb}>
                                {`${item.nome || item.tblValorDb} (${item.tblValorDb})`}
                              </MenuItem>
                            );
                          })
                      ) : (
                        <MenuItem key={`undefined`} value={undefined}>
                          Nenhum dado encontrado
                        </MenuItem>
                      )}
                      <MenuItem key={`${props.campo.nome}_null`} value={"-1"}>
                        {`Não atribuído`}
                      </MenuItem>
                    </Select>
                  </div>
                </Tooltip>
              </FormControl>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
